import React from 'react'
import Helmet from 'react-helmet'
import Link from 'gatsby-link'
import Layout from 'components/Layout'

const emergencyItems = [
  {
    title: 'Loose Crown',
    description:
      'It is normal to have tooth sensitivity if your crown came off loose. If you are not in moderate to severe pain, keep the area clean, and avoid eating on that side. Leave a message on our general answering machine and arrive at the office first thing the next business day. Please save your crown and bring it with you to your appointment in case it can be re-cemented.'
  },
  {
    title: 'Throbbing Tooth & Swelling',
    description:
      'If pain is mild to moderate, take 800mg Ibuprofen up to 3 times a day. And please call and leave a message with our answering machine and arrive at the office first thing the next working day.'
  },
  {
    title: 'Sore Mouth',
    description:
      'Rinse with warm salt water, avoid alcohol, carbonation and spicy foods. Please call 503-518-3384 and schedule an appointment to be seen.'
  },
  {
    title: 'Broken Tooth',
    description:
      'If you are not in pain, please keep area clean. Please call 503-518-3384 and leave a message and we will call you back to schedule an appointment and/or arrive at 8am the next open business day and we will work you in.'
  },
  {
    title: 'Loose or Broken Filling',
    description:
      'If you are not in pain, please keep area clean. Please call 503-518-3384 and leave a message and we will call you back to schedule an appointment or leave a message and arrive at 8am the next open business day and we will work you in. If you have mild to moderate pain, you can get over the counter temporary dental fill or dental wax from the pharmacy or grocery store for immediate relief.'
  },
  {
    title: 'Recent Extraction Pain',
    description:
      'It is common for a recent extraction area to be sore and even have moderate pain. This can continue for 3-5 days. This is due to swelling after the extraction from the body\u2019s natural healing process. Please take either 800mg Ibuprofen up to 3x per day or 500mg Tylenol 4x per day.'
  }
]

const postSurgicalDonts = [
  'Do not disturb the surgical site',
  'Do not rinse vigorously',
  'Do not suck on straws',
  'Do not smoke',
  'Do not drink alcohol',
  'Do not drink carbonated beverages',
  'Do not drink or eat anything that is hot',
  'Do not eat spicy foods',
  'Do not brush teeth next to surgical site (for the first 24 hours)',
  'Do not participate in vigorous exercise',
  'Do not spit'
]

const EmergencyCard = ({ title, description, index }) => (
  <div
    className="bg-white rounded-xl overflow-hidden transition-all duration-200 hover:shadow-xl"
    style={{
      boxShadow:
        '0 7px 14px 0 rgba(50, 50, 93, 0.1), 0 3px 6px 0 rgba(0, 0, 0, 0.07)',
      border: '1px solid #e3e8ee'
    }}
  >
    <div className="p-8">
      {/* Icon circle with number */}
      <div
        className="flex items-center justify-center mb-6"
        style={{
          width: '56px',
          height: '56px',
          minWidth: '56px',
          minHeight: '56px',
          borderRadius: '50%',
          background:
            index % 2 === 0
              ? 'linear-gradient(135deg, #635bff 0%, #635bffdd 100%)'
              : 'linear-gradient(135deg, #11efe3 0%, #11efe3dd 100%)'
        }}
      >
        <span className="text-white font-bold" style={{ fontSize: '1.25rem' }}>
          {index + 1}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-navy mb-3">{title}</h3>
      <p className="text-base text-textSecondary leading-relaxed">
        {description}
      </p>
    </div>
  </div>
)

const EmergencyCarePage = () => (
  <Layout>
    <Helmet
      title="Emergency Dental Care | Oregon City Smiles"
      meta={[
        {
          name: 'description',
          content:
            'Need emergency dental care in Oregon City? Oregon City Smiles provides urgent dental treatment for toothaches, broken teeth, and dental injuries. Call (503) 518-3384.'
        },
        {
          property: 'og:title',
          content: 'Emergency Dental Care | Oregon City Smiles'
        },
        {
          property: 'og:description',
          content:
            'Need emergency dental care in Oregon City? We provide urgent dental treatment for toothaches, broken teeth, and dental injuries.'
        }
      ]}
    />

    {/* Hero Section */}
    <section
      className="relative overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #0a2540 0%, #1a1f36 50%, #635bff 100%)',
        minHeight: '340px'
      }}
    >
      {/* Gradient Mesh Background */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at 20% 50%, #11efe3 0%, transparent 50%), radial-gradient(ellipse at 80% 80%, #ff80b5 0%, transparent 50%), radial-gradient(ellipse at 40% 20%, #635bff 0%, transparent 40%)'
        }}
      />

      <div
        className="relative z-10 container mx-auto px-6 flex flex-col items-center justify-center text-center"
        style={{
          minHeight: '340px',
          paddingTop: '5rem',
          paddingBottom: '5rem'
        }}
      >
        {/* Badge */}
        <div
          className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-6"
          style={{
            background:
              'linear-gradient(135deg, rgba(99, 91, 255, 0.1) 0%, rgba(17, 239, 227, 0.1) 100%)',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          Emergency Care
        </div>

        <h1
          className="text-white font-bold mb-4 leading-tight"
          style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            letterSpacing: '-0.03em'
          }}
        >
          After Hours Emergency Treatment
        </h1>

        <p
          className="text-lg leading-relaxed max-w-2xl mx-auto"
          style={{ color: 'rgba(255, 255, 255, 0.8)' }}
        >
          Whether it&apos;s after hours or you&apos;re unsure if it&apos;s an
          emergency, we&apos;re here to help. Review the guidance below or call
          us directly.
        </p>
      </div>
    </section>

    {/* No Pain to Moderate Pain Section */}
    <section className="py-24 px-6 bg-offWhite">
      <div className="container mx-auto max-w-6xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-4"
            style={{
              background:
                'linear-gradient(135deg, rgba(99, 91, 255, 0.1) 0%, rgba(17, 239, 227, 0.1) 100%)',
              color: '#635bff'
            }}
          >
            No Pain to Moderate Pain
          </div>
          <h2 className="text-4xl font-bold text-navy mb-4">
            Common Emergencies
          </h2>
          <p className="text-lg text-textSecondary max-w-2xl mx-auto">
            If you&apos;re experiencing mild to moderate discomfort, follow
            these guidelines and contact our office as soon as possible.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {emergencyItems.map((item, i) => (
            <EmergencyCard key={item.title} index={i} {...item} />
          ))}
        </div>
      </div>
    </section>

    {/* Severe Pain Callout */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-4xl">
        <div
          className="rounded-xl overflow-hidden"
          style={{
            boxShadow:
              '0 13px 27px -5px rgba(50, 50, 93, 0.25), 0 8px 16px -8px rgba(0, 0, 0, 0.3)',
            border: '1px solid #e3e8ee'
          }}
        >
          {/* Red-tinted gradient header */}
          <div
            className="px-8 py-6"
            style={{
              background: 'linear-gradient(135deg, #635bff 0%, #11efe3 100%)'
            }}
          >
            <h2 className="text-2xl font-bold text-white">Severe Pain</h2>
            <p
              className="text-sm font-medium mt-1"
              style={{ color: 'rgba(255, 255, 255, 0.85)' }}
            >
              If over-the-counter medication is not helping
            </p>
          </div>

          <div className="p-8">
            <p className="text-lg text-textSecondary leading-relaxed mb-6">
              If you are in severe pain, it is the weekend, and Ibuprofen is not
              helping, and you are a patient of record with Oregon City Smiles
              Dental, formerly Glenn Family Dental, then you may call our after
              hours line. Leave a detailed message with type of pain, location
              in mouth and duration. Someone will get back to you as soon as
              they can.
            </p>
            <div
              className="rounded-lg p-4"
              style={{
                background:
                  'linear-gradient(135deg, rgba(99, 91, 255, 0.05) 0%, rgba(17, 239, 227, 0.05) 100%)',
                border: '1px solid #e3e8ee'
              }}
            >
              <p className="text-sm text-textSecondary">
                <span className="font-semibold text-navy">Please note:</span> If
                a doctor has to come in after hours, there will be an emergency
                fee.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Post Surgical Care Section */}
    <section className="py-24 px-6 bg-offWhite">
      <div className="container mx-auto max-w-4xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-4"
            style={{
              background:
                'linear-gradient(135deg, rgba(99, 91, 255, 0.1) 0%, rgba(17, 239, 227, 0.1) 100%)',
              color: '#635bff'
            }}
          >
            Recovery Guide
          </div>
          <h2 className="text-4xl font-bold text-navy mb-4">
            Post Surgical Care
          </h2>
          <p className="text-lg text-textSecondary max-w-2xl mx-auto">
            Follow these instructions after your surgery to allow proper healing
            and minimize complications.
          </p>
        </div>

        {/* Content Card */}
        <div
          className="bg-white rounded-xl p-8 md:p-12"
          style={{
            boxShadow:
              '0 7px 14px 0 rgba(50, 50, 93, 0.1), 0 3px 6px 0 rgba(0, 0, 0, 0.07)',
            border: '1px solid #e3e8ee'
          }}
        >
          <p className="text-lg text-textSecondary leading-relaxed mb-6">
            After your surgery, it is important for your body&apos;s natural
            blood clot to form and remain to initiate the healing process.
            Please leave your gauze in for 30-45 minutes before removing it. You
            should not have to replace with new gauze unless you feel bleeding.
            If you do replace with new gauze, dampen the gauze with a couple
            drips of water to prevent sticking.
          </p>

          <h3 className="text-xl font-semibold text-navy mb-4">
            For the next 72 hours:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {postSurgicalDonts.map(item => (
              <div key={item} className="flex items-start">
                <div
                  className="flex items-center justify-center mr-3"
                  style={{
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    minHeight: '20px',
                    borderRadius: '50%',
                    marginTop: '3px',
                    background: 'rgba(99, 91, 255, 0.1)'
                  }}
                >
                  <svg
                    style={{ width: '10px', height: '10px' }}
                    fill="none"
                    stroke="#635bff"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <span className="text-base text-textSecondary">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-lg text-textSecondary leading-relaxed mb-6">
            If you want to gently rinse, lean over the sink and drool or wipe
            your mouth &mdash; do not spit. It is common to experience mild to
            moderate pain and swelling, which can last 48-72 hours. If you were
            prescribed medications, please use as directed.
          </p>

          <p className="text-lg text-textSecondary leading-relaxed mb-6">
            If you had a bone graft done, you may feel small particles in your
            mouth from time to time. This is normal. If you had stitches, please
            leave them alone as best as you can. Drink lots of fluids and keep
            to a soft nutritious diet. This can include smoothies, ice cream,
            mashed potatoes, and warm soups.
          </p>

          <div
            className="rounded-lg p-4"
            style={{
              background:
                'linear-gradient(135deg, rgba(99, 91, 255, 0.05) 0%, rgba(17, 239, 227, 0.05) 100%)',
              border: '1px solid #e3e8ee'
            }}
          >
            <p className="text-sm text-textSecondary">
              <span className="font-semibold text-navy">After 72 hours:</span>{' '}
              If you are noticing increased pain and/or swelling, please call
              the office at{' '}
              <a
                href="tel:503-518-3384"
                className="font-semibold text-primary hover:text-primaryDark transition-colors duration-150"
              >
                503-518-3384
              </a>{' '}
              and ask to speak with a dental assistant to go over possible
              complications.
            </p>
          </div>
        </div>

        {/* Insurance Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-textMuted leading-relaxed max-w-2xl mx-auto">
            Emergency Care policies generally cover only a portion of the total
            treatment cost. Unless other arrangements have been made, we ask
            that you pay your portion of the bill at the time of treatment. It
            is your responsibility to pay any balance not paid by your insurance
            company.
          </p>
        </div>
      </div>
    </section>

    {/* CTA Section */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto text-center max-w-3xl">
        {/* Decorative line */}
        <div
          className="mx-auto mb-8 rounded-full"
          style={{
            width: '64px',
            height: '4px',
            background: 'linear-gradient(90deg, #635bff 0%, #11efe3 100%)'
          }}
        />

        <h2 className="text-4xl font-bold text-navy mb-4 leading-tight">
          Need Immediate Help?
        </h2>

        <p className="text-lg text-textSecondary leading-relaxed mb-10 max-w-2xl mx-auto">
          Don&apos;t hesitate to reach out. Our team is ready to help you get
          the care you need.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="tel:503-518-3384"
            className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold text-base rounded-lg transition-all duration-150 hover:bg-primaryDark"
            style={{
              boxShadow: '0 4px 14px 0 rgba(99, 91, 255, 0.39)'
            }}
          >
            <svg
              className="mr-2"
              style={{ width: '18px', height: '18px' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            Call 503-518-3384
          </a>
          <Link
            to="/appointments"
            className="inline-flex items-center justify-center px-8 py-4 font-semibold text-base rounded-lg transition-all duration-150 text-textPrimary hover:bg-offWhite"
            style={{
              border: '1px solid #e3e8ee'
            }}
          >
            Schedule an Appointment
            <svg
              className="w-4 h-4 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  </Layout>
)

export default EmergencyCarePage
